import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Sun, Rocket, Compass as CompassIcon } from "lucide-react";
import useAuth from "../../hooks/useAuth";
import useRetryFetch from "../../hooks/useRetryFetch";
import { useIsMobile } from "../../hooks/use-mobile";
import { useForNow } from "../../hooks/useForNow";
import { config } from "../../Environment";
import TodayCard from "./TodayCard";
import LifeProjects from "./cockpit/LifeProjects";
import Compass from "./cockpit/Compass";
import { SeederInvite } from "./cockpit/LifeOsSeeder";
import { PlantedCard } from "./cockpit/PlantedCard";
import { useCockpitState } from "./cockpit/cockpitState";
import { plantedPhase, bumpPlantedVisits } from "../../lib/plantedLifecycle";
import { forNowTakeawayIds } from "../../lib/forNow";
import ContinueStrip from "../portal/myActivities/ContinueStrip";
import DiscoverSection from "./DiscoverSection";
import GuestHome from "./GuestHome";
import OnboardingProgramSection from "./OnboardingProgramSection";
import StudioHome from "./StudioHome";
import TodayTopLine from "./today/TodayTopLine";
import RhythmLine from "./today/RhythmLine";
import TodayCapture from "./today/TodayCapture";
import ForNowCard from "./today/ForNowCard";
import ReflectSection from "./today/ReflectSection";
import SectionDivider from "./today/SectionDivider";
import { isStudioUser } from "../../lib/userType";

const HomePage = () => {
  const { t } = useTranslation();
  const { auth, user } = useAuth();
  const isLoggedIn = !!auth?.email && !!auth?.accessToken;
  const isMobile = useIsMobile();
  const retryFetch = useRetryFetch();
  const [runningProcesses, setRunningProcesses] = useState<any[]>([]);
  const [runningLoaded, setRunningLoaded] = useState(false);
  const [fetchKey, setFetchKey] = useState(0);
  const captureRef = useRef<HTMLTextAreaElement>(null);
  const [mode, setModeState] = useState<CockpitMode>(() => {
    const stored = localStorage.getItem(MODE_KEY);
    return stored === "projects" || stored === "compass" ? stored : "today";
  });
  const setMode = (m: CockpitMode) => {
    setModeState(m);
    localStorage.setItem(MODE_KEY, m);
  };

  const cockpit = useCockpitState();
  const [visits, setVisits] = useState(0);
  const bumped = useRef(false);
  useEffect(() => {
    if (!bumped.current && !cockpit.isLoading && cockpit.seededAt) {
      bumped.current = true;
      setVisits(bumpPlantedVisits());
    }
  }, [cockpit.isLoading, cockpit.seededAt]);
  const planted = plantedPhase({
    seededAt: cockpit.seededAt,
    plantedCollapsedAt: cockpit.plantedCollapsedAt,
    visits,
  });

  // Coaches/experts (userType=expert) get the Studio home by default, with the
  // personal home one click away (persisted preference).
  const isStudio = isStudioUser(user);
  const [homeView, setHomeViewState] = useState<"studio" | "personal">(() =>
    localStorage.getItem(HOME_VIEW_KEY) === "personal" ? "personal" : "studio"
  );
  const setHomeView = (v: "studio" | "personal") => {
    setHomeViewState(v);
    localStorage.setItem(HOME_VIEW_KEY, v);
  };

  // Running processes for Continue and For now
  useEffect(() => {
    if (!isLoggedIn) return;
    const fetchRunning = async () => {
      try {
        const response = await retryFetch(
          `${config.API_BASE}/api/user/process/search?status=RUNNING&limit=20`
        );
        if (response.ok) {
          const data = await response.json();
          setRunningProcesses(data.list || data.processes || data || []);
        }
      } catch {
        // silently fail
      } finally {
        setRunningLoaded(true);
      }
    };
    fetchRunning();
  }, [isLoggedIn, fetchKey]);

  // Group flat processes into prescription-shaped objects for ContinueStrip
  const prescriptionsForStrip = useMemo(() => {
    const grouped: Record<string, any> = {};
    for (const proc of runningProcesses) {
      const key = proc.fiPrescription?._id || proc.fiPrescription || "unknown";
      if (!grouped[key]) {
        grouped[key] = {
          _id: key,
          name: proc.prescriptionName || "Activity",
          valid: true,
          processes: [],
          settings: {},
          engagementCount: 0,
        };
      }
      grouped[key].processes.push(proc);
      grouped[key].engagementCount++;
    }
    return Object.values(grouped);
  }, [runningProcesses]);

  // One "For now" suggestion (spec 2026-11-04 §5) — personal Today only.
  const forNow = useForNow({
    running: runningProcesses,
    runningLoaded,
    enabled: isLoggedIn && !(isStudio && homeView === "studio") && mode === "today",
  });
  const forNowFirst = forNow.choice.first;
  // Only hide what For now actually shows — while it is a skeleton, hide nothing.
  const excludeIds = useMemo(
    () => (forNow.ready ? forNowTakeawayIds(forNowFirst) : []),
    [forNow.ready, forNowFirst]
  );
  // Reflect skips cards in its own key space (`step:<takeawayId>`), not For now's.
  const skipKeys = useMemo(() => excludeIds.map((id) => `step:${id}`), [excludeIds]);

  if (!isLoggedIn) {
    return <GuestHome />;
  }

  if (isStudio && homeView === "studio") {
    return <StudioHome onSwitchToPersonal={() => setHomeView("personal")} />;
  }

  const firstName =
    user?.firstName || user?.displayName?.split(" ")[0] || auth.email?.split("@")[0] || "";

  const hasRunning = prescriptionsForStrip.length > 0;
  // New accounts get the full Explore under For now; everyone else a quiet footer.
  // Not "new" until both the cockpit and the running list have loaded, so an
  // existing account never flashes the full Explore.
  const isNewAccount = !cockpit.isLoading && runningLoaded && !cockpit.seededAt && !hasRunning;

  // The switch travels into whichever mode owns the header row — Projects and
  // Compass render their own page titles (5a/5b). It is always the LAST item
  // in the header cluster, so it sits in the same spot in every mode.
  const headerControls = <CockpitModeSwitch mode={mode} onChange={setMode} />;

  return (
    <div className="flex-1 overflow-y-auto scroll-smooth">
      <div className="max-w-[1220px] mx-auto px-6 md:px-8 py-6">
        {mode === "projects" ? (
          <LifeProjects headerExtra={headerControls} />
        ) : mode === "compass" ? (
          <Compass headerExtra={headerControls} />
        ) : (
          <>
            {/* Today (spec 2026-11-04 §2): one column. The upper part is a
                560px journal column; the lower part uses the full frame. */}
            <TodayTopLine
              seasonLine={cockpit.season?.line ?? null}
              rhythm={<RhythmLine />}
              controls={headerControls}
            />
            <div className="mx-auto mt-6 max-w-[560px] space-y-4">
              <TodayCapture firstName={firstName} inputRef={captureRef} />
              <ForNowCard
                choice={forNow.choice}
                ready={forNow.ready}
                onDismiss={forNow.dismiss}
                onWriteFirst={() => captureRef.current?.focus()}
              />
              <SeederInvite />
              {planted === "planted" && (
                <PlantedCard onOpenProjects={() => setMode("projects")} />
              )}
              <OnboardingProgramSection />
              {isNewAccount && <DiscoverSection />}
            </div>
            <SectionDivider ornament="leaf" className="mt-10" />
            {/* Two columns only when something is running; otherwise Today takes the full width. */}
            <div
              data-testid="today-continue-row"
              className={hasRunning ? "mt-6 grid grid-cols-1 gap-8 lg:grid-cols-[1.4fr_1fr]" : "mt-6"}
            >
              <div className="min-w-0">
                <TodayCard variant="slim" excludeIds={excludeIds} />
              </div>
              {hasRunning && (
                <div className="min-w-0">
                  <ContinueStrip
                    variant="list"
                    prescriptions={prescriptionsForStrip}
                    isMobile={isMobile}
                    refreshLists={() => setFetchKey((k) => k + 1)}
                  />
                </div>
              )}
            </div>
            <SectionDivider ornament="feather" className="mt-10" />
            <div className="mt-6">
              <ReflectSection skipKeys={skipKeys} />
            </div>
            {!isNewAccount && (
              <>
                <SectionDivider ornament="compass" className="mt-10" />
                <div className="mt-6">
                  <DiscoverSection variant="pills" />
                </div>
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
};

// ── Home mode switch — Today / Projects / Compass as three ways to look at
// the same life, not three separate apps. Compact: one line each. ───────────

type CockpitMode = "today" | "projects" | "compass";

const MODE_KEY = "cockpit_mode";
const HOME_VIEW_KEY = "home_view";

const CockpitModeSwitch = ({
  mode,
  onChange,
}: {
  mode: CockpitMode;
  onChange: (m: CockpitMode) => void;
}) => {
  const { t } = useTranslation();
  const modes: Array<{ key: CockpitMode; icon: React.ElementType; label: string }> = [
    { key: "today", icon: Sun, label: t("cockpit_mode_today") },
    { key: "projects", icon: Rocket, label: t("cockpit_mode_projects") },
    { key: "compass", icon: CompassIcon, label: t("cockpit_mode_compass") },
  ];
  return (
    <div className="inline-flex items-center gap-0.5 rounded-full border border-gray-200 bg-white/70 p-1">
      {modes.map(({ key, icon: Icon, label }) => (
        <button
          key={key}
          type="button"
          onClick={() => onChange(key)}
          aria-pressed={mode === key}
          className={
            mode === key
              ? "inline-flex items-center gap-1.5 rounded-full bg-primary text-primary-foreground px-3 min-h-[44px] text-sm font-medium"
              : "inline-flex items-center gap-1.5 rounded-full px-3 min-h-[44px] text-sm font-medium text-muted-foreground hover:text-foreground"
          }
        >
          <Icon className="hidden sm:inline w-3.5 h-3.5" aria-hidden />
          {label}
        </button>
      ))}
    </div>
  );
};

export default HomePage;
